import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  useTheme,
  InputAdornment,
  alpha,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Category as CategoryIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import PublicLayout from '@/layouts/PublicLayout';
import { formInputStyles, formButtonStyles } from '@/styles/formStyles';

const contactReasons = [
  {
    id: 'business_partnership',
    label: 'Organization Looking to Explore Partnership with Dreamz Connect',
  },
  {
    id: 'school_inquiry',
    label: 'School Administrator Inquiring about Student Opportunities',
  },
  {
    id: 'student_signup',
    label: 'Student Looking to Start Volunteering',
  },
  {
    id: 'requesting_demo',
    label: 'Requesting a Demo of Dreamz Connect Platform',
  },
  {
    id: 'general_inquiry',
    label: 'General Question or Information Request',
  },
] as const;

interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  reason: string;
  message: string;
}

const initialFormData: ContactFormData = {
  first_name: '',
  last_name: '',
  email: '',
  reason: '',
  message: ''
};

declare global {
  interface Window {
    Botpoison: any;
  }
}

const ContactPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handle reason from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reasonParam = params.get('reason');
    if (reasonParam && contactReasons.some(r => r.id === reasonParam)) {
      setFormData(prev => ({ ...prev, reason: reasonParam }));
    }
  }, [location.search]);

  // Initialize Botpoison
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@botpoison/browser';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get Botpoison solution
      const botpoison = new window.Botpoison({
        publicKey: import.meta.env.VITE_BOTPOISON_PUBLIC_KEY
      });
      const { solution } = await botpoison.challenge();
      
      // Get the reason label for the email
      const reasonLabel = contactReasons.find(r => r.id === formData.reason)?.label || formData.reason;
      
      // Submit form with Botpoison solution
      const response = await fetch(import.meta.env.VITE_FORMSPARK_FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _botpoison: solution,
          _email: {
            from: formData.email,
            subject: `Contact Form: ${reasonLabel}`,
            message: `
Name: ${formData.first_name} ${formData.last_name}
Email: ${formData.email}
Reason for Contact: ${reasonLabel}

Message:
${formData.message}
            `
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      setNotification({
        open: true,
        message: 'Message sent successfully! We will get back to you soon.',
        severity: 'success'
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNotification({
        open: true,
        message: 'Failed to send message. Please try again later.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Validate required environment variables
  useEffect(() => {
    if (!import.meta.env.VITE_FORMSPARK_FORM_URL) {
      console.error('Missing VITE_FORMSPARK_FORM_URL environment variable');
    }
    if (!import.meta.env.VITE_BOTPOISON_PUBLIC_KEY) {
      console.error('Missing VITE_BOTPOISON_PUBLIC_KEY environment variable');
    }
  }, []);

  return (
    <PublicLayout>
      <Box sx={{ 
        bgcolor: '#F7FAFC',
        py: { xs: 4, md: 8 }
      }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            align="center"
            sx={{ 
              fontWeight: 700,
              mb: 6,
              color: 'text.primary'
            }}
          >
            Contact Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.4,
                  ease: "easeOut"
                }}
                sx={{
                  p: 4,
                  height: '100%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  borderRadius: '24px',
                  color: 'white',
                }}
              >
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Get in Touch
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 4,
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: 1.7,
                  }}
                >
                  Have questions about Dreamz Connect? Contact us to learn more!
                </Typography>

                <Stack spacing={4}>
                  {/* Contact information */}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.4,
                  ease: "easeOut"
                }}
                sx={{
                  p: 4,
                  background: alpha(theme.palette.background.paper, 0.9),
                  backdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  ...formInputStyles(theme),
                }}
              >
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="First Name" 
                        name="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="Last Name" 
                        name="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        label="Email" 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel id="contact-reason-label">Reason for Contact</InputLabel>
                        <Select
                          labelId="contact-reason-label"
                          id="contact-reason"
                          value={formData.reason}
                          label="Reason for Contact"
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          startAdornment={
                            <InputAdornment position="start">
                              <CategoryIcon color="action" />
                            </InputAdornment>
                          }
                        >
                          {contactReasons.map((reason) => (
                            <MenuItem key={reason.id} value={reason.id}>
                              {reason.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        multiline
                        rows={4}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MessageIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={isSubmitting}
                        sx={formButtonStyles(theme)}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PublicLayout>
  );
};

export default ContactPage;